import React from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { AddButton } from "../../../utils/Custom/Button";

const AttendanceExcelReport = ({
  attendanceList = [],
  attendanceRegularizationList = [],
  leaveManagementList = [],
  weekOffs = ["Sunday"],
  holidayList = [],
  companyName = "S AND R GEOTECHNIQUES PRIVATE LIMITED",
}) => {
  const getLocalDateKey = (date) => new Date(date).toLocaleDateString("en-CA"); // YYYY-MM-DD (local)

  if (!attendanceList.length && !attendanceRegularizationList.length)
    return null;

  const holidaySet = new Set(holidayList.map((h) => getLocalDateKey(h.date)));

  // ================= LEAVE SHORT FORMS =================
  const leaveShortForms = {
    "Sick Leave": "SL",
    "Casual Leave": "CL",
    "Privileged Leave": "PL",
    "Maternity Leave": "ML",
    "Paternity Leave": "PTL",
    "Menstrual Leave": "MSL",
  };

  // ================= LEAVE MAP CREATION =================
  const leaveMap = {};

  leaveManagementList.forEach((leave) => {
    if (leave.approveLevel2 !== 2) return;

    const empId = leave.employeeId;
    if (!leaveMap[empId]) leaveMap[empId] = {};

    const start = new Date(leave.fromDate);
    const end = new Date(leave.toDate);

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const key = getLocalDateKey(d);

      let shortForm = leaveShortForms[leave.leaveType] || "L";

      const isHalfDay =
        leave.fromLeaveValue.includes("Half") ||
        leave.toLeaveValue.includes("Half");

      leaveMap[empId][key] = {
        label: isHalfDay ? shortForm + "-H" : shortForm,
        isHalf: isHalfDay,
        type: leave.leaveType,
      };
    }
  });

  const dates = attendanceList
    .map((a) => a.checkinDatetime)
    .filter(Boolean)
    .map((d) => new Date(d));

  if (!dates.length) return;

  const minDate = new Date(Math.min(...dates));
  const maxDate = new Date(Math.max(...dates));

  const allDates = [];
  for (let d = new Date(minDate); d <= maxDate; d.setDate(d.getDate() + 1)) {
    allDates.push(new Date(d));
  }

  const getHours = (i, o) => (i && o ? (new Date(o) - new Date(i)) / 36e5 : 0);

  // ================= MAIN STATUS FUNCTION =================
  const getStatus = (dateObj, rec, employeeId) => {
    // const key = dateObj.toISOString().split("T")[0];

    const key = getLocalDateKey(dateObj);

    const day = dateObj.toLocaleString("en-US", { weekday: "long" });

    const isHoliday = holidaySet.has(key);
    const isWeekOff = weekOffs.includes(day);

    const hours = getHours(rec?.in, rec?.out);

    let workStatus = null;

    if (rec?.forceMSP) workStatus = "MSP";
    else if (rec?.in && !rec?.out) workStatus = "MSP";
    else if (hours >= 6) workStatus = "P";
    else if (hours >= 4) workStatus = "HD";
    else workStatus = "AB";

    if (isHoliday) workStatus = hours >= 6 ? "P" : "HO";
    if (isWeekOff) workStatus = hours >= 6 ? "P" : "WO";

    const leaveData =
      leaveMap[employeeId] && leaveMap[employeeId][key]
        ? leaveMap[employeeId][key]
        : null;

    // ----- COMBINE HD + HALF LEAVE -----
    if (workStatus === "HD" && leaveData && leaveData.isHalf) {
      return {
        label: `HD + ${leaveData.label}`,
        combined: true,
        workPart: "HD",
        leavePart: leaveData,
      };
    }

    if (leaveData) return leaveData;

    return workStatus;
  };

  const formatDateHeader = (date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "short" });
    const weekday = date.toLocaleString("en-US", { weekday: "short" });
    return `${day} ${month} (${weekday})`;
  };

  // ================= EXCEL DOWNLOAD =================
  const downloadExcel = () => {
    const empMap = {};

    attendanceList.forEach((a) => {
      if (!empMap[a.employeeId]) {
        empMap[a.employeeId] = {
          code: a.employeesCode,
          name: a.employeeName,
          records: {},
        };
      }

      // const key = new Date(a.checkinDatetime).toISOString().split("T")[0];

      const key = getLocalDateKey(a.checkinDatetime);

      // empMap[a.employeeId].records[key] = {
      //   in: a.checkinDatetime,
      //   out: a.checkoutDatetime,
      // };

      const existing = empMap[a.employeeId].records[key];

      if (!existing || a.id > existing.id) {
        empMap[a.employeeId].records[key] = {
          id: a.id,
          in: a.checkinDatetime,
          out: a.checkoutDatetime,
          forceMSP: a.forceMSP,
        };
      }
    });

    const sheet = [];

    sheet.push([companyName]);
    sheet.push(["Attendance Report"]);
    sheet.push([`From ${minDate.toDateString()} To ${maxDate.toDateString()}`]);
    sheet.push([]);

    const summaryCols = [
      "P",
      "HD",
      "MSP",
      "AB",
      "WO",
      "HO",
      "PLV",
      "UPL",
      "TL",
      "Total WD",
    ];

    sheet.push([
      "Emp Code",
      "Emp Name",
      ...allDates.map(formatDateHeader),
      "",
      ...summaryCols,
    ]);

    Object.entries(empMap).forEach(([empId, emp]) => {
      let P = 0,
        HD = 0,
        MSP = 0,
        AB = 0,
        WO = 0,
        HO = 0,
        paidLeaves = 0,
        unpaidLeaves = 0;

      const row = [emp.code, emp.name];

      allDates.forEach((d) => {
        // const key = d.toISOString().split("T")[0];

        const key = getLocalDateKey(d);

        let statusObj = getStatus(d, emp.records[key], empId);

        let displayStatus =
          typeof statusObj === "object" ? statusObj.label : statusObj;

        row.push(displayStatus);

        // -------- SUMMARY LOGIC --------

        if (typeof statusObj === "object" && statusObj.combined) {
          HD += 1;

          const leaveType = statusObj.leavePart.type;
          const isHalf = 0.5;

          if (leaveType === "Casual Leave") {
            unpaidLeaves += isHalf;
          } else {
            paidLeaves += isHalf;
          }
        } else if (displayStatus === "P") P++;
        else if (displayStatus === "HD") HD++;
        else if (displayStatus === "MSP") MSP++;
        else if (displayStatus === "WO") WO++;
        else if (displayStatus === "HO") HO++;
        else if (displayStatus === "AB") AB++;
        else if (typeof statusObj === "object") {
          const isHalf = statusObj.isHalf ? 0.5 : 1;

          if (statusObj.type === "Casual Leave") {
            unpaidLeaves += isHalf;
          } else {
            paidLeaves += isHalf;
          }
        }
      });

      const totalLeaves = paidLeaves + unpaidLeaves;

      const totalWorkingDays = P + MSP + HD / 2 + paidLeaves;

      row.push(
        "",
        P,
        HD,
        MSP,
        AB,
        WO,
        HO,
        paidLeaves,
        unpaidLeaves,
        totalLeaves,
        totalWorkingDays.toFixed(1),
      );

      sheet.push(row);
    });

    sheet.push([]);

    const legendRow1 = [
      "'P' for Present Days, 'HD' for Half-day, 'WO' for Week-off days, 'UPL' for Unpaid leave",
    ];

    const legendRow2 = [
      "'HO' for Holidays, 'AB' for Absent Days, 'MSP' for Missed Punch",
    ];

    const leaveLegendRow = [
      "Leave Short Forms Used → SL: Sick Leave, CL: Casual Leave, PL: Privileged Leave, ML: Maternity Leave, PTL: Paternity Leave, MSL: Menstrual Leave | Summary Columns → PLV: Paid Leaves, UPL: Unpaid Leaves, TL: Total Leaves",
    ];

    sheet.push(legendRow1);
    sheet.push(legendRow2);
    sheet.push(leaveLegendRow);

    const ws = XLSX.utils.aoa_to_sheet(sheet);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Attendance");

    saveAs(
      new Blob([
        XLSX.write(wb, {
          bookType: "xlsx",
          type: "array",
        }),
      ]),
      "Attendance_Report.xlsx",
    );
  };

  return (
    <AddButton onClick={downloadExcel} label="Download Attendance Excel" />
  );
};

export default AttendanceExcelReport;
